import { type SearchClient, SearchIndex } from 'algoliasearch';
import { SearchResponse } from 'instantsearch.js';
import { HitProp } from '../types';
import { transformAlgoliaHitsToCategoryProducts } from './algoliaHelper';
import { Product } from '../../../types/category/category';

type FacetValues = {
  value: string;
  count: number;
};

export type FilterFacets = {
  [key: string]: FacetValues[];
};

export const getIndex = (client: SearchClient, indexName: string): SearchIndex => client.initIndex(indexName);

export const getIndexSettings = async (index: SearchIndex) => index.getSettings();

export const mapAttributesForFaceting = (attributes: readonly string[]): string[] =>
  attributes.map((attr: string) => attr.replace(/^(searchable|filterOnly)\((.*)\)$/, '$2'));

const getFilteredItems = async (
  client: SearchClient,
  indexName: string,
  category: string = '',
  getHits = false,
): Promise<SearchResponse<HitProp>> => {
  const index = getIndex(client, indexName);
  const settings = await getIndexSettings(index);

  return index.search<HitProp>(category, {
    filters: `productCategory.slug:${category}`,
    restrictSearchableAttributes: ['productCategory.slug'],
    facets: mapAttributesForFaceting(settings.attributesForFaceting ?? []),
    maxValuesPerFacet: 100,
    hitsPerPage: getHits ? 100 : 0,
  });
};

export const getFilteredFacets = (facets: SearchResponse<HitProp>['facets']): FilterFacets => {
  const hiddenFacets = ['locale', 'productCategory.slug']; // System facets
  return Object.entries(facets ?? {}).reduce<FilterFacets>((acc, [attribute, values]) => {
    if (!hiddenFacets.includes(attribute)) {
      const facetValues = Object.entries(values as Record<string, number>)
        .map(([value, count]) => ({ value, count }))
        .filter(({ count }) => count > 0);

      if (facetValues.length >= 2) {
        acc[attribute] = facetValues;
      }
    }
    return acc;
  }, {});
};

export const getIndexFacets = async (
  client: SearchClient,
  indexName: string,
  category: string = '',
): Promise<SearchResponse<HitProp>['facets']> => {
  const { facets } = await getFilteredItems(client, indexName, category);

  if (!facets) {
    return {};
  }

  return facets;
};

export const getFilterProducts = async (
  client: SearchClient,
  indexName: string,
  category: string = '',
): Promise<Product[]> => {
  const { hits } = await getFilteredItems(client, indexName, category, true);

  if (!hits) {
    return [];
  }

  return transformAlgoliaHitsToCategoryProducts(hits);
};
